import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./styles";
import classNames from "classnames";
import ExploreBenefits from "../explore-benefits";
import ContinueShopping from "../continue-shopping";
import CloseSVG from "../../assets/closeButton.svg";
import { formatHeaderWords } from "../../helpers/formatHeaderWords";
import { setShowSuccess } from "../../state/join-loyalty/reducer";
import { useJoinStatus } from "../../context/join-status";
import { useLocaleProvider, useTranslate } from "../../context/locale-provider";

const returnTranslations = (tier, translate) => {
  const translationsMap = {
    1: {
      header: translate(
        "loyalty_landing_page_join_success_screen_title_stylist"
      ),
      welcome: translate(
        "loyalty_landing_page_join_success_screen_body_stylist"
      ),
    },
    2: {
      header: translate(
        "loyalty_landing_page_join_success_screen_title_curator"
      ),
      welcome: translate(
        "loyalty_landing_page_join_success_screen_body_curator"
      ),
    },
    3: {
      header: translate("loyalty_landing_page_join_success_screen_title_icon"),
      welcome: translate("loyalty_landing_page_join_success_screen_body_icon"),
    },
    4: {
      header: translate(
        "loyalty_landing_page_join_success_screen_title_alister"
      ),
      welcome: translate(
        "loyalty_landing_page_join_success_screen_body_alister"
      ),
    },
  };
  return translationsMap[tier];
};

const SuccessOverlay = ({ loyaltyData, currentTierId }) => {
  const translate = useTranslate(useLocaleProvider());
  const closeButtonAccessibility = translate(
    "loyalty_landing_page_accessibility_close_button_description"
  );

  const [, dispatch] = useJoinStatus();
  const [closeModal, setCloseModal] = useState(false);
  const [fadeOut, setfadeOut] = useState(false);
  const { tiers, benefits } = loyaltyData;

  const fadeOutFunctionality = useCallback(() => {
    setfadeOut(true);

    setTimeout(function () {
      setCloseModal(true);
      dispatch(setShowSuccess(false));
    }, 300);
  }, [dispatch]);

  const fadeOutFunctionalityDesktop = useCallback(
    (e) => {
      if (
        window.matchMedia &&
        window.matchMedia("(min-width: 480px)").matches &&
        e?.target?.className.includes("successContainer")
      ) {
        fadeOutFunctionality();
      }
    },
    [fadeOutFunctionality]
  );

  useEffect(() => {
    const loyaltyFaqPage = document.getElementById("loyaltyFaqPage");
    loyaltyFaqPage?.addEventListener("click", fadeOutFunctionalityDesktop);
    return () => {
      loyaltyFaqPage?.removeEventListener("click", fadeOutFunctionalityDesktop);
    };
  }, [fadeOutFunctionalityDesktop]);

  const currentTierData = tiers.filter((tier) => {
    return tier.id === currentTierId;
  })[0];

  const handleOnClick = () => {
    fadeOutFunctionality();
  };

  const isAlister = currentTierData.tierNumber === 4;
  const setAlisterBackground = (isAlister) =>
    isAlister ? "backgroundBlack" : "backgroundWhite";
  const setAlisterColor = (isAlister, isGrey) =>
    isAlister ? "colorBlack" : isGrey ? "colorGrey" : "colorWhite";

  const translationData = returnTranslations(
    currentTierData.tierNumber,
    translate
  );
  const { header, welcome } = translationData;
  const headerFormatted = formatHeaderWords(header, true);

  return (
    <>
      {!closeModal && (
        <div className={styles.successContainer}>
          <div
            className={classNames(
              { [styles.fadeOut]: fadeOut },
              styles.successBox,
              styles[setAlisterBackground(isAlister)]
            )}
          >
            <div
              className={classNames(
                styles.successCloseContainer,
                styles[isAlister ? "closeBlack" : "closeWhite"]
              )}
            >
              <button
                aria-label={closeButtonAccessibility}
                onClick={handleOnClick}
              >
                <CloseSVG alt={closeButtonAccessibility} />
              </button>
            </div>
            <div
              className={classNames(
                styles.successImage,
                styles[`tier${currentTierData.tierNumber}`]
              )}
            >
              <div
                className={classNames(
                  styles.header,
                  styles.headerASOSWorld,
                  styles[setAlisterColor(isAlister)]
                )}
              >
                <h1
                  className={styles[setAlisterBackground(!isAlister)]}
                  aria-label={header}
                >
                  {headerFormatted}
                </h1>
              </div>
            </div>
            <div
              className={classNames(
                styles.benefitsText,
                styles[setAlisterColor(!isAlister, true)]
              )}
            >
              <div>{welcome}</div>
              <ul className={styles.benefitList}>
                {benefits
                  .filter((benefit) => {
                    return currentTierData.tierBenefitIds.includes(benefit.id);
                  })
                  .map((benefit) => {
                    const { name } = benefit;
                    return <li key={name}>{name}</li>;
                  })}
              </ul>
            </div>
            <div className={styles.buttonLinksContainer}>
              <ExploreBenefits
                optedInButtonText={translate(
                  "loyalty_landing_page_join_success_screen_primary_cta"
                )}
              />
              <ContinueShopping isAlister={isAlister} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

SuccessOverlay.propTypes = {
  loyaltyData: PropTypes.object,
  currentTierId: PropTypes.string,
};

export default SuccessOverlay;
